import { SearchOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { AutoComplete, Button, Card, Checkbox, Col, DatePicker, Divider, Form, Input, InputNumber, message, Row, Select, Space, Switch, Table, Tabs, Tag, Typography } from "antd";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { EntityAttachmentsPanel } from "../../shared/components/EntityAttachmentsPanel";
import dayjs from "dayjs";
import { transportApi } from "../../api/transportApi";
import { getDefaultContext } from "../../api/httpClient";
import type { VehicleDto } from "../../types/api";
import { getEnteredByUser } from "../../utils/currentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { SaveActionButton } from "../../shared/components/SaveActionButton";

const VEHICLE_TYPES = [
  "Open Truck", "Closed Truck", "Container Truck", "Trailer",
  "Tanker", "Refrigerated Truck", "Mini Truck", "Pickup", "Tempo", "Other",
];

const FUEL_TYPES = ["Diesel", "Petrol", "CNG", "LNG", "Electric", "Hybrid"];

const BODY_TYPES = ["Open Body", "Closed Body", "Half-Body", "Container", "Tanker", "Tipper", "Flat Bed", "Other"];

const OWNERSHIP_TYPES = [
  { value: "CompanyOwned", label: "Company Owned" },
  { value: "VendorOwned", label: "Vendor Owned" },
  { value: "Rental", label: "Rental" },
];

export const FleetListPage = () => {
  const [rows, setRows] = useState<VehicleDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [form] = Form.useForm();
  const [drawerTab, setDrawerTab] = useState("details");

  const load = async () => {
    setLoading(true);
    try {
      const result = await transportApi.listVehicles({ page: 1, pageSize: 500, q: search.trim() || undefined });
      setRows(result.data);
      setPage(1);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load fleet registry"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      row.vehicleNo.toLowerCase().includes(term) ||
      row.vehicleType.toLowerCase().includes(term) ||
      row.fuelType.toLowerCase().includes(term) ||
      (row.make ?? "").toLowerCase().includes(term) ||
      (row.model ?? "").toLowerCase().includes(term)
    );
  }, [rows, search]);

  const onCreateOrUpdate = async () => {
    const values = await form.validateFields();
    const ctx = getDefaultContext();
    const payload = {
      ...ctx,
      enteredBy: getEnteredByUser(),
      vehicleNo: values.vehicleNo,
      vehicleType: values.vehicleType,
      capacity: values.capacity,
      fuelType: values.fuelType,
      chassisNo: values.chassisNo,
      engineNo: values.engineNo,
      make: values.make,
      model: values.model,
      bodyType: values.bodyType,
      colour: values.colour,
      size: values.size,
      registeredAt: values.registeredAt,
      ownershipType: values.ownershipType ?? "CompanyOwned",
      ownerPartyId: values.ownerPartyId ?? null,
      taxTokenNo: values.taxTokenNo,
      taxTokenIssueFrom: values.taxTokenIssueFrom ? values.taxTokenIssueFrom.toISOString() : null,
      taxTokenValidUpto: values.taxTokenValidUpto ? values.taxTokenValidUpto.toISOString() : null,
      roadPermitNo: values.roadPermitNo,
      roadPermitDate: values.roadPermitDate ? values.roadPermitDate.toISOString() : null,
      roadPermitStateValid: values.roadPermitStateValid,
      insuranceCo: values.insuranceCo,
      insuranceDivisionNo: values.insuranceDivisionNo,
      insCertificateNo: values.insCertificateNo,
      insCertificateDt: values.insCertificateDt ? values.insCertificateDt.toISOString() : null,
      panNo: values.panNo,
      isTdsApplicable: values.isTdsApplicable ?? false,
      hasRC: values.hasRC ?? false,
      hasForm15I: values.hasForm15I ?? false,
      insuranceExpiry: values.insuranceExpiry ? values.insuranceExpiry.toISOString() : null,
      fitnessExpiry: values.fitnessExpiry ? values.fitnessExpiry.toISOString() : null,
      permitExpiry: values.permitExpiry ? values.permitExpiry.toISOString() : null,
      isActive: values.isActive,
      remarks: values.remarks,
    };

    try {
      setSaving(true);
      if (editingId) {
        await transportApi.updateVehicle(editingId, { ...payload, id: editingId });
        message.success("Vehicle updated");
      } else {
        await transportApi.createVehicle(payload);
        message.success("Vehicle added");
      }
      setOpen(false);
      setEditingId(null);
      form.resetFields();
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to save vehicle"));
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row: VehicleDto) => {
    setDrawerTab("details");
    setEditingId(row.id);
    form.setFieldsValue({
      vehicleNo: row.vehicleNo,
      vehicleType: row.vehicleType,
      capacity: row.capacity,
      fuelType: row.fuelType,
      chassisNo: row.chassisNo,
      engineNo: row.engineNo,
      make: row.make,
      model: row.model,
      bodyType: row.bodyType,
      colour: row.colour,
      size: row.size,
      registeredAt: row.registeredAt,
      ownershipType: row.ownershipType ?? "CompanyOwned",
      ownerPartyId: row.ownerPartyId,
      taxTokenNo: row.taxTokenNo,
      taxTokenIssueFrom: row.taxTokenIssueFrom ? dayjs(row.taxTokenIssueFrom) : undefined,
      taxTokenValidUpto: row.taxTokenValidUpto ? dayjs(row.taxTokenValidUpto) : undefined,
      roadPermitNo: row.roadPermitNo,
      roadPermitDate: row.roadPermitDate ? dayjs(row.roadPermitDate) : undefined,
      roadPermitStateValid: row.roadPermitStateValid,
      insuranceCo: row.insuranceCo,
      insuranceDivisionNo: row.insuranceDivisionNo,
      insCertificateNo: row.insCertificateNo,
      insCertificateDt: row.insCertificateDt ? dayjs(row.insCertificateDt) : undefined,
      panNo: row.panNo,
      isTdsApplicable: row.isTdsApplicable,
      hasRC: row.hasRC,
      hasForm15I: row.hasForm15I,
      insuranceExpiry: row.insuranceExpiry ? dayjs(row.insuranceExpiry) : undefined,
      fitnessExpiry: row.fitnessExpiry ? dayjs(row.fitnessExpiry) : undefined,
      permitExpiry: row.permitExpiry ? dayjs(row.permitExpiry) : undefined,
      isActive: row.isActive,
      remarks: row.remarks,
    });
    setOpen(true);
  };

  return (
    <Card className="panel-card">
      <div className="panel-title-row">
        <Typography.Title level={3}>Fleet Registry</Typography.Title>
        <Button
          type="primary"
          onClick={() => { setEditingId(null); form.resetFields(); form.setFieldsValue({ isActive: true, ownershipType: "CompanyOwned" }); setOpen(true); }}
        >
          Add Vehicle
        </Button>
      </div>

      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} wrap>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search vehicle no, type, fuel, make, model..."
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 380 }}
        />
        <Typography.Text type="secondary">
          Showing {filteredRows.length} of {rows.length} loaded vehicles
        </Typography.Text>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={filteredRows}
        size="small"
        pagination={{
          current: page,
          pageSize,
          total: filteredRows.length,
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} vehicles`,
          onChange: (nextPage, nextPageSize) => { setPage(nextPage); setPageSize(nextPageSize); }
        }}
        columns={[
          { title: "#", width: 60, render: (_, __, index) => ((page - 1) * pageSize) + index + 1 },
          { title: "Vehicle No", dataIndex: "vehicleNo", width: 130 },
          { title: "Type", dataIndex: "vehicleType" },
          { title: "Make/Model", render: (_, row) => [row.make, row.model].filter(Boolean).join(" ") || "—" },
          { title: "Cap (T)", dataIndex: "capacity", align: "right", width: 80 },
          { title: "Fuel", dataIndex: "fuelType", width: 80 },
          { title: "Ownership", dataIndex: "ownershipType", width: 120, render: (v: string) => v?.replace(/([A-Z])/g, " $1").trim() },
          {
            title: "Status", dataIndex: "isActive", width: 80,
            render: (v: boolean) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>
          },
          { title: "", width: 80, render: (_, row) => <Button size="small" onClick={() => onEdit(row)}>Edit</Button> },
        ]}
      />

      <StaticDrawer
        title={editingId ? "Edit Vehicle" : "Add Vehicle"}
        open={open}
        onClose={() => { setOpen(false); setDrawerTab("details"); }}
        width={800}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => { setOpen(false); setDrawerTab("details"); }}>Cancel</Button>
            {drawerTab === "details" && (
              <SaveActionButton saving={saving} idleText={editingId ? "Update" : "Save"} savingText={editingId ? "Updating..." : "Saving..."} onClick={() => void onCreateOrUpdate()} />
            )}
          </div>
        }
        maskClosable={false}
      >
        <Tabs
          activeKey={drawerTab}
          onChange={setDrawerTab}
          size="small"
          style={{ marginTop: -8 }}
          items={[
            {
              key: "details",
              label: "Vehicle Details",
              children: (
        <Form form={form} layout="vertical" initialValues={{ isActive: true, ownershipType: "CompanyOwned" }}>

          <Divider orientation="left" plain>Basic Information</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="vehicleNo" label="Vehicle No" rules={[{ required: true }]}>
                <Input placeholder="e.g. MH12AB1234" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="vehicleType" label="Vehicle Type" rules={[{ required: true }]}>
                <AutoComplete
                  options={VEHICLE_TYPES.map(v => ({ value: v }))}
                  placeholder="Select or type..."
                  filterOption={(input, opt) => (opt?.value ?? "").toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="capacity" label="Capacity (Tons)" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} min={0} step={0.5} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="fuelType" label="Fuel Type" rules={[{ required: true }]}>
                <AutoComplete
                  options={FUEL_TYPES.map(v => ({ value: v }))}
                  placeholder="Select or type..."
                  filterOption={(input, opt) => (opt?.value ?? "").toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="make" label="Make">
                <Input placeholder="e.g. Tata, Ashok Leyland" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="model" label="Model">
                <Input placeholder="e.g. 407, 1109" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="bodyType" label="Body Type">
                <AutoComplete
                  options={BODY_TYPES.map(v => ({ value: v }))}
                  filterOption={(input, opt) => (opt?.value ?? "").toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="colour" label="Colour">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="size" label="Size">
                <Input placeholder="e.g. 20ft, 32ft" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="chassisNo" label="Chassis No">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="engineNo" label="Engine No">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="registeredAt" label="Registered At (RTO)">
                <Input placeholder="e.g. Mumbai West" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Ownership</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="ownershipType" label="Ownership Type">
                <Select options={OWNERSHIP_TYPES} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="panNo" label="PAN No">
                <Input maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={8} style={{ paddingTop: 30 }}>
              <Form.Item name="isTdsApplicable" valuePropName="checked">
                <Checkbox>TDS Applicable</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="hasRC" valuePropName="checked">
                <Checkbox>Has RC</Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="hasForm15I" valuePropName="checked">
                <Checkbox>Has Form 15-I</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Tax Token</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="taxTokenNo" label="Tax Token No">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="taxTokenIssueFrom" label="Issue From">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="taxTokenValidUpto" label="Valid Upto">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Road Permit</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="roadPermitNo" label="Road Permit No">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="roadPermitDate" label="Permit Date">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="roadPermitStateValid" label="States Valid">
                <Input placeholder="e.g. MH, GJ, MP" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Insurance</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="insuranceCo" label="Insurance Company">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="insuranceDivisionNo" label="Division No">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="insCertificateNo" label="Certificate No">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="insCertificateDt" label="Certificate Date">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="insuranceExpiry" label="Insurance Expiry">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Document Expiries</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="fitnessExpiry" label="Fitness Expiry">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="permitExpiry" label="Permit Expiry">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8} style={{ paddingTop: 30 }}>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
              )
            },
            {
              key: "documents",
              label: "Documents",
              disabled: !editingId,
              children: (
                <EntityAttachmentsPanel
                  entityType="vehicle"
                  entityId={editingId}
                />
              )
            }
          ]}
        />
      </StaticDrawer>
    </Card>
  );
};
